import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { AssetUrls } from 'config/assets';
import { SEOHelmet, StructuredData, BreadcrumbList } from 'components/SEO';
import { useGetPublishedArticlesQuery } from 'store/services/blog/blogApi';
import { BlogArticleType } from 'types/interfaces/blog';
import './ArticlesHub.css';

interface PinnedArticle {
  title: string;
  description: string;
  readTime: string;
  hero: string;
  route: string;
  alt: string;
  buttonText?: string;
}

const PINNED_ARTICLES: PinnedArticle[] = [
  {
    title: 'How TradeMagic Works',
    description: 'A complete quick-start guide with everything you need to know about what makes TradeMagic special.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/login/login-background.png',
    route: '/articles/how-it-works',
    alt: 'TradeMagic background with magical cards'
  },
  {
    title: 'Shipping Guide',
    description: 'Instructions for how to mail Magic cards safely and easily.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.shippingGuide,
    route: '/articles/shipping',
    alt: 'Blue-themed wizard character with shipping supplies',
    buttonText: 'Read'
  },
  {
    title: 'Sustainable Economics',
    description: 'How we manage the long-term health of TradeMagic.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/hero_sensei_divining_top.png',
    route: '/articles/sustainable-economics',
    alt: 'Sensei\'s Divining Top card representing economic management',
    buttonText: 'Read'
  },
  {
    title: 'Condition Guide',
    description: 'Instructions for how to evaluate the condition of Magic cards.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.conditionGuide,
    route: '/articles/condition-guide',
    alt: 'Futuristic crystalline city representing card quality standards',
    buttonText: 'Read'
  }
];

interface CmsArticleCard {
  title: string;
  description: string;
  readTime: string;
  hero: string;
  route: string;
  alt: string;
  buttonText: string;
  articleType: BlogArticleType;
  contentHtml: string;
}

const ArticlesHub = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState<'BLOG' | 'GUIDE'>('BLOG');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch CMS articles
  const { data: cmsArticlesData } = useGetPublishedArticlesQuery({
    page: 1,
    limit: 100,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
  });

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Map CMS articles to card format
  const cmsArticles: CmsArticleCard[] = useMemo(() =>
    (cmsArticlesData?.data || []).map((article: any) => ({
      title: article.title,
      description: article.subtitle || '',
      readTime: '',
      hero: article.heroImageUrl || '',
      route: `/articles/${article.slug}`,
      alt: article.title,
      buttonText: 'Read',
      articleType: article.articleType || 'BLOG',
      contentHtml: article.contentHtml || '',
    })),
    [cmsArticlesData]
  );

  const isSearching = searchQuery.trim().length > 0;

  // Split by type (both always rendered in DOM for crawlability)
  const blogArticles = useMemo(() =>
    cmsArticles.filter(a => a.articleType === 'BLOG'),
    [cmsArticles]
  );
  const guideArticles = useMemo(() =>
    cmsArticles.filter(a => a.articleType === 'GUIDE'),
    [cmsArticles]
  );

  // Search across all CMS articles
  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = searchQuery.toLowerCase();
    return cmsArticles.filter(a => {
      const plainText = a.contentHtml.replace(/<[^>]*>/g, '');
      return a.title.toLowerCase().includes(q) || plainText.toLowerCase().includes(q);
    });
  }, [cmsArticles, searchQuery, isSearching]);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Articles & Resources - TradeMagic"
        description="Learn how to trade Magic cards on TradeMagic. Guides on card conditions, shipping, and sustainable trading economics."
        canonical="https://trademagic.gg/articles"
        keywords="MTG trading guides, Magic card condition guide, MTG shipping guide, trade Magic cards, TradeMagic resources"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
        ]}
      />

      {/* CollectionPage Schema */}
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Helpful Articles",
          "description": "Your HQ for TradeMagic resources",
          "url": "https://trademagic.gg/articles",
          "publisher": {
            "@type": "Organization",
            "name": "TradeMagic",
            "url": "https://trademagic.gg"
          }
        }}
      />

      <div className="articles-hub-layout">

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="articles-hub-mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="articles-hub-content-wrapper">
        <LeftNavigationBar
          currentTab={{ path: '/articles', Component: <></> }}
          isPublicMode={!currentUser}
          onNavigationAttempt={handleNavigationAttempt}
          isAuthenticated={!!currentUser}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />

        <div className="articles-hub-main">
          {/* Top Navigation Bar */}
          <TopNavigationBar
            title="Articles"
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />

          <div className="articles-hub-container">
            {/* Page Header */}
            <header className="articles-hub-header">
              <h1 className="articles-hub-title">Helpful Articles</h1>
              <h2 className="articles-hub-subtitle">Your HQ for TradeMagic resources</h2>
            </header>

            {/* Pinned Articles Section */}
            <section className="articles-hub-pinned-section">
              <h3 className="articles-hub-section-title">Essential Guides</h3>
              <div className="articles-hub-pinned-grid">
                {PINNED_ARTICLES.map((article) => (
                  <Link
                    key={article.route}
                    to={article.route}
                    className="articles-hub-card articles-hub-pinned-card"
                  >
                    <div
                      className="articles-hub-card-hero"
                      style={{ backgroundImage: `url(${article.hero})` }}
                    >
                      <div className="articles-hub-card-overlay" />
                      <img
                        src={article.hero}
                        alt={article.alt}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="articles-hub-card-content">
                      <div className="articles-hub-card-header">
                        <h3 className="articles-hub-card-title">{article.title}</h3>
                        {article.readTime && (
                          <span className="articles-hub-card-read-time">{article.readTime}</span>
                        )}
                      </div>
                      <p className="articles-hub-card-description">{article.description}</p>
                      <span className="articles-hub-card-button">{article.buttonText || 'Learn More'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Search Bar */}
            <div className="articles-hub-search">
              <div className="articles-hub-search-wrapper">
                <svg className="articles-hub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  className="articles-hub-search-input"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="articles-hub-search-clear"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            {/* Tab Row (hidden when searching) */}
            {!isSearching && (
              <div className="articles-hub-tabs">
                <button
                  className={`articles-hub-tab ${activeTab === 'BLOG' ? 'active' : ''}`}
                  onClick={() => setActiveTab('BLOG')}
                >
                  Blog
                </button>
                <button
                  className={`articles-hub-tab ${activeTab === 'GUIDE' ? 'active' : ''}`}
                  onClick={() => setActiveTab('GUIDE')}
                >
                  Guides
                </button>
              </div>
            )}

            {/* Search Results (shown when searching) */}
            {isSearching && (
              <>
                {searchResults.length > 0 ? (
                  <section className="articles-hub-grid">
                    {searchResults.map((article) => (
                      <Link
                        key={article.route}
                        to={article.route}
                        className="articles-hub-card"
                      >
                        <div
                          className="articles-hub-card-hero"
                          style={{ backgroundImage: `url(${article.hero})` }}
                        >
                          <div className="articles-hub-card-overlay" />
                          <span className="articles-hub-type-label">
                            {article.articleType === 'BLOG' ? 'Blog' : 'Guide'}
                          </span>
                          <img
                            src={article.hero}
                            alt={article.alt}
                            style={{ display: 'none' }}
                          />
                        </div>
                        <div className="articles-hub-card-content">
                          <div className="articles-hub-card-header">
                            <h3 className="articles-hub-card-title">{article.title}</h3>
                          </div>
                          <p className="articles-hub-card-description">{article.description}</p>
                          <span className="articles-hub-card-button">{article.buttonText || 'Read'}</span>
                        </div>
                      </Link>
                    ))}
                  </section>
                ) : (
                  <div className="articles-hub-empty">No articles found for &ldquo;{searchQuery}&rdquo;</div>
                )}
              </>
            )}

            {/* Blog Tab Content — both tabs always in DOM for SEO crawlability */}
            <section className={`articles-hub-grid articles-hub-tab-content ${!isSearching && activeTab === 'BLOG' ? 'active' : ''}`}>
              {blogArticles.length > 0 ? (
                blogArticles.map((article) => (
                  <Link
                    key={article.route}
                    to={article.route}
                    className="articles-hub-card"
                  >
                    <div
                      className="articles-hub-card-hero"
                      style={{ backgroundImage: `url(${article.hero})` }}
                    >
                      <div className="articles-hub-card-overlay" />
                      <img
                        src={article.hero}
                        alt={article.alt}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="articles-hub-card-content">
                      <div className="articles-hub-card-header">
                        <h3 className="articles-hub-card-title">{article.title}</h3>
                      </div>
                      <p className="articles-hub-card-description">{article.description}</p>
                      <span className="articles-hub-card-button">Read</span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="articles-hub-empty">No blog posts yet.</div>
              )}
            </section>

            {/* Guide Tab Content */}
            <section className={`articles-hub-grid articles-hub-tab-content ${!isSearching && activeTab === 'GUIDE' ? 'active' : ''}`}>
              {guideArticles.length > 0 ? (
                guideArticles.map((article) => (
                  <Link
                    key={article.route}
                    to={article.route}
                    className="articles-hub-card"
                  >
                    <div
                      className="articles-hub-card-hero"
                      style={{ backgroundImage: `url(${article.hero})` }}
                    >
                      <div className="articles-hub-card-overlay" />
                      <img
                        src={article.hero}
                        alt={article.alt}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="articles-hub-card-content">
                      <div className="articles-hub-card-header">
                        <h3 className="articles-hub-card-title">{article.title}</h3>
                      </div>
                      <p className="articles-hub-card-description">{article.description}</p>
                      <span className="articles-hub-card-button">Read</span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="articles-hub-empty">No guides yet.</div>
              )}
            </section>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />

      <Footer />
      </div>
    </>
  );
};

export default ArticlesHub;
